import apiClient from './client';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  display_name: string;
  username?: string;
  location?: string;
  connection_type?: string;
}

export interface AuthResponse {
  // With cookie-based auth (Issue #289), tokens are in httpOnly cookies, not response body
  message: string;
  user: {
    id: string;
    email: string;
    display_name: string;
    friend_code?: string;
    sharing_enabled: boolean;
    vpn_ip?: string;
  };
  is_new_user?: boolean;  // True if user was just created via OAuth
}

export interface PasswordResetRequest {
  email: string;
}

export interface PasswordResetConfirm {
  token: string;
  new_password: string;
}

export interface EmailVerificationRequest {
  email: string;
}

export interface EmailVerificationConfirm {
  token: string;
}

export interface OAuthLoginRequest {
  provider: 'google' | 'apple' | 'github';
  token: string;
  display_name?: string;
  location?: string;
}

export interface DeviceApproveRequest {
  user_code: string;
}

export interface GitHubInitiateResponse {
  state: string;
}

export interface GitHubCallbackRequest {
  code: string;
  state: string;
}

export interface GoogleInitiateResponse {
  state: string;
}

export interface GoogleCallbackRequest {
  code: string;
  redirect_uri: string;
  state: string;
}

export interface AppleInitiateResponse {
  state: string;
}

export interface AppleCallbackRequest {
  code: string;
  redirect_uri: string;
  state: string;
}

export const authApi = {
  login: (data: LoginRequest) =>
    apiClient.post<AuthResponse>('/api/v1/auth/login', data),

  register: (data: RegisterRequest) =>
    apiClient.post<{ id: string; email: string; display_name: string }>('/api/v1/auth/register', data),

  getCurrentUser: () =>
    apiClient.get('/api/v1/auth/me'),

  logout: () =>
    apiClient.post('/api/v1/auth/logout'),

  requestPasswordReset: (data: PasswordResetRequest) =>
    apiClient.post<{ message: string }>('/api/v1/auth/request-password-reset', data),

  resetPassword: (data: PasswordResetConfirm) =>
    apiClient.post<{ message: string }>('/api/v1/auth/reset-password', data),

  requestEmailVerification: (data: EmailVerificationRequest) =>
    apiClient.post<{ message: string }>('/api/v1/auth/request-email-verification', data),

  verifyEmail: (data: EmailVerificationConfirm) =>
    apiClient.post<{ message: string }>('/api/v1/auth/verify-email', data),

  oauthLogin: (data: OAuthLoginRequest) =>
    apiClient.post<AuthResponse>('/api/v1/auth/oauth/login', data),

  deviceApprove: (data: DeviceApproveRequest) =>
    apiClient.post<{ message: string }>('/api/v1/auth/device/approve', data),

  deviceDeny: (data: DeviceApproveRequest) =>
    apiClient.post<{ message: string }>('/api/v1/auth/device/deny', data),

  githubInitiate: () =>
    apiClient.get<GitHubInitiateResponse>('/api/v1/auth/oauth/github/initiate'),

  githubCallback: (data: GitHubCallbackRequest) =>
    apiClient.post<AuthResponse>('/api/v1/auth/oauth/github/callback', data),

  googleInitiate: () =>
    apiClient.get<GoogleInitiateResponse>('/api/v1/auth/oauth/google/initiate'),

  googleCallback: (data: GoogleCallbackRequest) =>
    apiClient.post<AuthResponse>('/api/v1/auth/oauth/google/callback', data),

  appleInitiate: () =>
    apiClient.get<AppleInitiateResponse>('/api/v1/auth/oauth/apple/initiate'),

  appleCallback: (data: AppleCallbackRequest) =>
    apiClient.post<AuthResponse>('/api/v1/auth/oauth/apple/callback', data),
};